import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@cartcloser/ui';
import { formatMicros, getConversation } from '../lib/api.js';

export const Route = createFileRoute('/admin/$slug/conversations/$id')({
  component: ConversationDetailPage,
});

function ConversationDetailPage() {
  const { slug, id } = Route.useParams();
  const query = useQuery({
    queryKey: ['conversation', slug, id],
    queryFn: () => getConversation(slug, id),
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (query.isError || !query.data)
    return <p className="text-sm text-destructive">Couldn’t load this conversation.</p>;

  const { conversation, messages, cost } = query.data;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link
          to="/admin/$slug/conversations"
          params={{ slug }}
          className="text-sm text-primary hover:underline"
        >
          ← Conversations
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">
          {conversation.expertiseRouted ?? 'Unrouted'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {new Date(conversation.startedAt).toLocaleString()}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Call cost</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          {cost.local ? (
            <p className="text-lg font-semibold text-muted-foreground">
              <s>{formatMicros(cost.totalMicros)}</s>{' '}
              <span className="text-sm font-normal">free · ran locally on Ollama</span>
            </p>
          ) : (
            <p className="text-lg font-semibold">{formatMicros(cost.totalMicros)}</p>
          )}
          {cost.models.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              Model{cost.models.length > 1 ? 's' : ''}: {cost.models.join(', ')}
            </p>
          ) : null}
          {cost.breakdown ? (
            <div className="text-xs text-muted-foreground">
              {Object.entries(cost.breakdown).map(([kind, micros]) => (
                <span key={kind} className="mr-3">
                  {kind}: {formatMicros(micros)}
                </span>
              ))}
              <p className="mt-1 italic">Breakdown visible to platform admins only.</p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="space-y-4">
        {messages.map((m) => (
          <div key={m.id} className={m.role === 'user' ? 'text-right' : 'text-left'}>
            {m.role === 'system_internal' ? (
              <div className="inline-block rounded-md bg-muted px-3 py-1 text-xs italic text-muted-foreground">
                internal: {m.content}
              </div>
            ) : (
              <div
                className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2 text-sm ${
                  m.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}
              >
                {m.content}
                {m.role === 'assistant' && m.feedback ? (
                  <span className="ml-2 align-middle" title="Shopper feedback">
                    {m.feedback > 0 ? '👍' : '👎'}
                  </span>
                ) : null}
              </div>
            )}
            {m.recommendations.length > 0 ? (
              <div className="mt-1 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Recommended:</p>
                {m.recommendations.map((r) => (
                  <div
                    key={r.position}
                    className="rounded-md border border-border px-3 py-1.5 text-xs"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span>
                        <Badge variant="outline" className="mr-2">
                          #{r.position}
                        </Badge>
                        {r.title} <span className="font-mono text-muted-foreground">{r.sku}</span>
                      </span>
                      {r.clicked ? <Badge variant="success">Clicked</Badge> : null}
                    </div>
                    {r.reason ? <p className="mt-1 text-muted-foreground">{r.reason}</p> : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
